<template>
    <div class="delivery-details">
        <div class="delivery-details--content">
            <h2 class="delivery-details--content--title">{{$t('REGISTER_DELIVERY_DETAILS_TITLE')}}</h2>
            <p class="delivery-details--content--text">{{$t('REGISTER_DELIVERY_DETAILS_TEXT')}}</p>

            <!-- Postcode -->
            <postcode-lookup-field
                class="contentsquare_sensitive_data"
                ref="postcode"
                :mandatory="true"
                address-type="shipping"
                v-model="deliveryAddress.postcode"
                :errorHandler="errorHandler"
                :showLabel="false"
                :noPadding="true"
                @clear-address="clearAddress"
                @address-selected="updateSelectedAddress"/>

                <p
                     class="delivery-details--content--postcode-link"
                    @click="showAddress = !showAddress"
                    v-if="!showAddress"> {{$t('DELIVERY_ADDRESS_ENTER_MANUALLY')}}
                </p>

            <!-- Address line 1 -->
            <div>
                <input-field
                    class="mb-3 contentsquare_sensitive_data"
                    :noPadding="true"
                    id="delivery_address_line_1"
                    v-show="showAddress"
                    :mandatory="true"
                    v-if="showAddress"
                    autocomplete="shipping address-line1"
                    v-model="deliveryAddress.addressLine1"
                    :is-valid="!errorHandler.has('delivery_address_line_1')"
                    :message="errorHandler.get('delivery_address_line_1')"
                    :label="$t('DELIVERY_ADDRESS_FIRST_LINE')"/>
            </div>

            <!-- Address line 2 -->
            <div>
                <input-field
                    class="mb-3 contentsquare_sensitive_data"
                    :noPadding="true"
                    v-show="showAddress"
                    autocomplete="shipping address-line2"
                    v-model="deliveryAddress.addressLine2"
                    :label="$t('DELIVERY_ADDRESS_SECOND_LINE')"/>
            </div>

            <!-- City -->
            <div>
                <input-field
                    class="mb-3 contentsquare_sensitive_data"
                    :noPadding="true"
                    id="delivery_address_city"
                    v-show="showAddress"
                    :mandatory="true"
                    v-if="showAddress"
                    autocomplete="shipping address-level2"
                    v-model="deliveryAddress.city"
                    :is-valid="!errorHandler.has('delivery_address_city')"
                    :message="errorHandler.get('delivery_address_city')"
                    :label="$t('DELIVERY_ADDRESS_TOWN_OR_CITY')"/>
            </div>

            <delivery-waiting-list-modal
                :show-modal="showUndeliverablePostcodeModal"
                @closed="toggleUndeliverablePostcodeModal"
                :postcode="deliveryAddress.postcode"/>
        </div>

        <!-- Delivery summary -->
        <div class="delivery-details--summary">

            <div class="delivery-details--summary--image">
                <img :src="getImagetUrl" alt="Delivery Vehical image">
            </div>

            <div class="delivery-details--summary--content">
                <p class="delivery-details--summary--text" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_FIRST_DELIVERY')}}</p>
                <h2 class="delivery-details--summary--day" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_DELIVERY_DAY', {deliveryDate})}}</h2>
                <p class="delivery-details--summary--text" v-if="deliveryDay" v-html="$t('REGISTER_DELIVERY_DETAILS_FRESH_FLOWERS', {deliveryDay})"></p>
                <p class="delivery-details--summary--text" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_CHANGE_DELIVERY_DAY')}}</p>
                <p v-if="postcodeError" v-text="postcodeError"/>
                <p v-else-if="!deliveryDay" class="delivery-details--summary--text" v-html="$t('DELIVERY_FORM_POP_IN')"/>
                <div class="delivery-details--summary--day-select" v-if="showDeliveryDaySelector">
                    <p class="delivery-details--summary--text">{{ $t('DELIVERY_FORM_PREFERRED_DELIVERY_DAY') }}</p>
                    <multiselect
                        class="delivery-details--summary--day-select--dropdown"
                        v-model="preferredDeliveryDay"
                        openDirection="below"
                        :searchable="false"
                        :show-labels="false"
                        :options="weekDayOptions"
                        :allow-empty="false"
                        />
                </div>
            </div>
        </div>

        <!-- property type -->
        <div class="delivery-details--property-type">
            <p class="delivery-details--property-type--text"> {{$t('REGISTER_DELIVERY_PROPERTY')}}</p>
            <custom-select
                class="contentsquare_sensitive_data"
                :defaultOption="propertyType"
                trackBy="name"
                :options="propertyTypes"
                :simpleSelect="true"
                v-model="selectedProperty"/>

            <p class="delivery-details--property-type--error"
                v-if="errorHandler.has('delivery_address_property_type')"
                v-text="errorHandler.get('delivery_address_property_type')"/>

            <p class="delivery-details--property-type--comment">{{$t('REGISTER_DELIVERY_PROPERTY_COMMENT')}}</p>
            <!--Delivery comment-->
            <textbox-field
                id="delivery_instructions"
                class="delivery-details--property-type--input contentsquare_sensitive_data"
                :is-valid="!errorHandler.has('delivery_instructions')"
                :message="errorHandler.get('delivery_instructions')"
                v-model="deliveryComment"/>
        </div>

        <!-- Phone Number -->
        <div class="delivery-details--phone">
            <p class="delivery-details--phone--text">{{$t('REGISTER_DELIVERY_PHONE_TEXT')}}</p>
            <telephone-field
                class="contentsquare_sensitive_data"
                v-model="telephone"
                :showLabel="false"
                id="telephone"
                :mandatory="true"
                :is-valid="!errorHandler.has('telephone')"
                :message="errorHandler.get('telephone')"
                :should-validate="true"/>
        </div>

        <div class="delivery-details--next-button">
            <submit-button
                :processing="isFormProcessing"
                color="green"
                @click="submitCurrentStep()">
                {{ $t('ACCOUNT_FORM_NEXT_STEP') }}
            </submit-button>
        </div>
        <alt-trust-pilot step="delivery" class="delivery-details--trust-pilot"/>
        <simple-footer />
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { couriers } from '@/content/couriers';
import { isPostcodeFormatValid } from '@/helpers/PostcodeHelpers';
import RegistrationService from '@/services/users/RegistrationService';
import IncompleteRegistrationService from '@/services/users/incompleteRegistrationService/IncompleteRegistrationService';
import InputField from '@/components/forms/fields/InputField';
import PostcodeService from '@/services/logistics/PostcodeService';
import DeliveryWaitingListModal from '@/components/modals/DeliveryWaitingListModal';
import PostcodeLookupField from '@/components/forms/fields/PostcodeLookupField';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import CustomSelect from '@/components/partials/register/CustomSelect';
import TextboxField from '@/components/forms/fields/TextboxField';
import TelephoneField from '@/components/forms/fields/TelephoneField';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import SimpleFooter from '@/components/structure/SimpleFooter';
import AltTrustPilot from '@/components/forms/fields/AltTrustPilot';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';

export default {
    name: 'DeliveryDetails',
    components: {
        PostcodeLookupField,
        InputField,
        DeliveryWaitingListModal,
        Multiselect,
        CustomSelect,
        TextboxField,
        TelephoneField,
        SubmitButton,
        SimpleFooter,
        AltTrustPilot,
    },
    data() {
        return {
            showUndeliverablePostcodeModal: false,
            showAddress: false,
            displayPlanDropdown: true,
            errorHandler: new ErrorResponseHandler(),
            postcodeError: null,
            weekDayOptions: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            selectedProperty: null,
            isFormProcessing: false,
        };
    },
    computed: {
        courierName: {
            get() {
                return this.$store.getters['register/getCourierName'];
            },
            set(value) {
                this.$store.commit('register/setCourierName', value);
            },
        },
        telephone: {
            get() {
                return this.$store.getters['register/getTelephone'];
            },
            set(value) {
                this.$store.commit('register/setTelephone', value);
            },
        },
        getImagetUrl() {
            let name = couriers[this.courierName] ? couriers[this.courierName].icon : 'van';
            return require(`@/assets/images/decoration/icon-delivery-${name}.svg`);
        },
        courierDisplayName() {
            return couriers[this.courierName] ? couriers[this.courierName].displayName : this.courierName;
        },
        deliveryDate() {
            const deliveryDate = this.$store.getters['register/getFirstPossibleDeliveryDate'];
            return deliveryDate && formatDate(deliveryDate, Dates.DAY_DATE_MONTH);
        },
        deliveryDay() {
            if (this.showDeliveryDaySelector && this.preferredDeliveryDay) return this.preferredDeliveryDay;
            return this.$store.getters['register/getFirstDeliveryDay'];
        },
        propertyTypes() {
            return this.$store.getters['register/getPropertyTypes'];
        },
        selectedDeliveryDate: {
            get() {
                return this.$store.getters['register/getFirstDeliveryDateDesired'];
            },
            set(value) {
                this.$store.commit('register/setFirstDeliveryDateDesired', value);
            },
        },
        deliveryComment: {
            get() {
                return this.$store.getters['register/getDeliveryComment'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryComment', value);
            },
        },
        showDeliveryDaySelector() {
            return process.env.VUE_APP_DELIVERY_DAY_SELECTOR === 'true' && this.courierName === 'DPD';
        },
        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },
        firstPossibleDeliveryDate: {
            get() {
                return this.$store.getters['register/getFirstPossibleDeliveryDate'];
            },
            set(value) {
                this.$store.commit('register/setFirstPossibleDeliveryDate', value);
            },
        },
        preferredDeliveryDay: {
            get() {
                return this.$store.getters['register/getPreferredDeliveryDay'];
            },
            set(value) {
                this.$store.commit('register/setPreferredDeliveryDay', value);
            },
        },
        propertyType() {
            return this.propertyTypes.filter(item => item.name === this.$store.getters['register/getPropertyType'])[0];
        },
    },
    mounted() {
        this.selectedProperty = this.propertyType;
    },
    methods: {
        updateSelectedAddress(address) {
            this.deliveryAddress.addressLine1 = address.addressline1;
            this.deliveryAddress.addressLine2 = address.addressline2;
            this.deliveryAddress.city = address.county;
            this.showAddress = true;
            this.fetchDeliveryDateFromApi(this.deliveryAddress.postcode);
        },
        toggleUndeliverablePostcodeModal(value) {
            this.showUndeliverablePostcodeModal = value || !this.showUndeliverablePostcodeModal;
        },
        clearAddress(value) {
            if (value) {
                this.deliveryAddress.addressLine1 = null;
                this.deliveryAddress.addressLine2 = null;
                this.deliveryAddress.city = null;
            }
        },
        fetchDeliveryDateFromApi(postcode) {
            if (isPostcodeFormatValid(postcode)) {
                PostcodeService.GetFirstDeliveryDayForPostcode(postcode)
                    .then(response => {
                        const data = response.data.properties;
                        this.firstPossibleDeliveryDate = data.first_delivery_day;
                        this.courierName = data.courier_name;
                        this.preferredDeliveryDay = this.deliveryDay;
                    })
                    .catch(error => {
                        const response = error.response.data;

                        if (response.errors?.postcode) {
                            this.postcodeError = response.errors.postcode[0];
                        }
                    });
            }
        },
        submitCurrentStep() {
            this.isFormProcessing = true;

            setTimeout(() => (new IncompleteRegistrationService()).updateOrCreate(), 0);
            RegistrationService.validateDelivery()
                .then(() => {
                    this.isFormProcessing = false;
                    this.$router.push({ name: 'register.your-details' });
                })
                .catch(error => {
                    this.showAddress = true;
                    this.errorHandler.setResponse(error.response && error.response.data);
                    this.isFormProcessing = false;
                    const errorElementsArray = Object.keys(this.errorHandler.response.errors);
                    const errorElementId = errorElementsArray.includes('delivery_address_postcode') ||
                        errorElementsArray.includes('delivery_address_line_1') ||
                        errorElementsArray.includes('delivery_address_city')
                        ? 'delivery_address_line_1' : errorElementsArray[0];
                    this.$scrollTo(`#${errorElementId}`, {
                        offset: -200,
                    });
                });
        },
    },
    watch: {
        selectedProperty(value) {
            this.$store.commit('register/setPropertyType', value?.name);
        },
        deliveryAddress: {
            deep: true,
            handler(address) {
                this.$emit('input', address);
            },
        },
        'deliveryAddress.postcode': {
            deep: true,
            handler: function(newPostcode, oldPostcode) {
                newPostcode = (newPostcode || '').toLowerCase();
                oldPostcode = (oldPostcode || '').toLowerCase();

                if ((oldPostcode !== newPostcode)) {
                    this.postcodeError = null;
                    this.firstPossibleDeliveryDate = null;
                    this.fetchDeliveryDateFromApi(newPostcode);
                    this.$emit('postcodeChanged');
                    if (this.$refs.postcode.isValid) {
                        PostcodeService.fetchPostcodeDeliverableStatus(newPostcode)
                            .catch(() => this.toggleUndeliverablePostcodeModal(true));
                    }
                }
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.delivery-details {
    margin-top: 20px;
    &--content {
        width: 90%;
        @media (max-width: 640px) {
            width: 95%;
        }
        margin: 20px auto;
        &--title {
            font-weight: 400;
            font-size: 25px;
            line-height: 130%;
            color: #262323;
        }
        &--text {
            font-size: 16px;
            line-height: 140%;
            color: #262323;
        }
        &--postcode-link {
            font-size: 14px;
            line-height: 25px;
            text-align: center;
            text-decoration-line: underline;
            color: #262323;
        }
    }
    &--summary {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: #F7F7F7;
        border-radius: 4px;
        margin: 40px auto;
        padding-bottom: 20px;
        &--content {
            width: 70%;
            margin: 0 auto;
        }
        &--image {
            width: 100px;
            margin-top: -15px;
        }
        &--text {
            font-size: 14px;
            line-height: 140%;
            color: #262323;
            text-align: center;
        }
        &--day {
            color: #262323;
            text-align: center;
            font-size: 20px;
            line-height: 25px;
        }
        &--day-select {
            margin: 20px auto;
            &--dropdown {
                width: 50%;
                max-width: 300px;
                min-width: 150px;
                margin: 10px auto;
            }
        }
    }
    &--property-type {
        margin: 20px auto;
        &--comment {
            padding: 0 10px;
            font-size: 16px;
            line-height: 140%;
            color: #262323;
            margin-bottom: 0;
            margin-top: 40px;
        }
        &--text {
            font-size: 16px;
            line-height: 140%;
        }
        &--input {
            margin-top: -20px;
        }
        &--error {
            font-size: 15px;
            line-height: 15px;
            color: #b1473c;
        }
    }
    &--phone {
        margin: 20px auto;
        &--text {
            padding: 0 10px;
            font-size: 16px;
            line-height: 140%;
            color: #262323;
            margin: 40px 0 20px 0;
        }
    }
    &--next-button {
        width: 80%;
        margin: 20px auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    &--trust-pilot {
        width: 100%;
        margin-top: 70px;
    }
}
</style>
